// useCluster.ts
import { useCallback, useEffect, useState } from 'react'
import {
  ClusterInterface,
  ClusterPayload,
  MetricsDetailed,
  MetricsSummary,
} from '../../../../common/types/interfaces'
import { getSalesApi } from '../../../../salesApi/salesApi'
import { schedulePattern } from '../../../../common/utils/constants'
import { getApi } from '../../../../api/api'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentCluster,
  setCurrentCluster,
} from '../../../../store/slices/currentClusterSlice'
import { openPayment } from '../../../../store/slices/paymentSlice'

const initialValue: ClusterPayload = {
  name: '',
  description: '',
  withDelivery: false,
  withBonuses: false,
  phone_number: '',
  disabledTemporary: false,
  type: '',
  writeOff: '',
  forceStore: false,
  schedule: [...schedulePattern],
  delivery: [],
  bonuses: [],
  complexSales: false,
  regularSales: true,
  legalName: '',
  legalInn: '',
  legalAddress: '',
  legalEntityType: -1,
  isActive: false,
  agreement: '',
  domain: '',
  yookassa: { enabled: false },
  paymaster: { enabled: false },
  payment: {
    cashOnDelivery: true,
    cardOnDelivery: true,
    onlineOnDelivery: true,
    cashOnPickup: true,
    cardOnPickup: true,
    onlineOnPickup: true,
    billOnDelivery: false,
    billOnPickup: false,
  },
  showRating: true,
  manageStock: false,
  showOutOfStock: false,
  onePrice: true,
  cdek: {
    enabled: false,
    insurance: false,
    markup:'',
    pickup: true,
    courier: true
  },
   cutlery: false
}

export const useCluster = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [data, setData] = useState<ClusterPayload>(initialValue)
  const [list, setList] = useState<ClusterInterface[]>([])
  const [statsModalActive, setStatsModalActive] = useState<boolean>(false)
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [syncModalActive, setSyncModalActive] = useState<boolean>(false)
  const [current, setCurrent] = useState<ClusterInterface>()
  const [api] = useState(getApi())
  const [salesApi] = useState(getSalesApi())
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [metricsMap, setMetricsMap] = useState<Record<string, MetricsSummary>>({})
  const [detailedMetrics, setDetailedMetrics] = useState<MetricsDetailed | null>(null)
  const [extendModalActive, setExtendModalActive] = useState<boolean>(false)

  const currentCluster = useSelector(selectCurrentCluster)

  const getList = useCallback(async () => {
    try {
      const result = await api.getClusterList()
      if (result) {
        setList(result.data)

        const metricsPromises = result.data
          .filter((c: ClusterInterface) => c.domain)
          .map(async (cluster) => {
            const summary = await salesApi.getMetricsSummary(cluster.domain!)
            return { domain: cluster.domain, metrics: summary }
          })

        const metricsResults = await Promise.all(metricsPromises)
        const map: Record<string, MetricsSummary> = {}
        metricsResults.forEach((m) => {
          if (m && m.metrics) map[m.domain!] = m.metrics
        })
        setMetricsMap(map)
      }
    } catch (error) {
      // log
    } finally {
      setLoading(false)
    }
  }, [api, salesApi])

  const startCreate = (qr?: string) => {
    setData((prev) => ({ ...prev, barCode: qr }))
    setModalActive(true)
  }

  const startEdit = (el: ClusterInterface) => {
    setCurrent({ ...el })
    setEdit(true)
    setModalActive(true)
  }

  const showDetails = useCallback((el: ClusterInterface) => {
    setCurrent(el)
    setDetailsModalActive(true)
  }, [])

  const showStats = useCallback(
    async (el: ClusterInterface) => {
      setCurrent(el)
      setStatsModalActive(true)
      if (el.domain) {
        const detailed = await salesApi.getMetricsDetailed(el.domain)
        setDetailedMetrics(detailed)
      }
    },
    [salesApi],
  )

  const switchCluster = useCallback(
    (el: ClusterInterface) => {
      dispatch(setCurrentCluster(el))
      salesApi.setClusterUrl(el)
    },
    [dispatch, salesApi],
  )

  const handleOpenPayment = useCallback(
    (el: ClusterInterface) => {
      dispatch(openPayment({ clusterId: el.id!, mode: 'cluster' }))
    },
    [dispatch],
  )

  const handleOpenExtend = useCallback((el: ClusterInterface) => {
    setCurrent(el)
    setExtendModalActive(true)
  }, [])

  useEffect(() => {
    getList()
  }, [getList])

  useEffect(() => {
    if (!modalActive) setData(initialValue)
  }, [modalActive])

  return {
    getList,
    modalActive,
    setModalActive,
    startCreate,
    startEdit,
    edit,
    data,
    setData,
    list,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    switchCluster,
    loading,
    syncModalActive,
    setSyncModalActive,
    openPayment: handleOpenPayment,
    metricsMap,
    showStats,
    statsModalActive,
    setStatsModalActive,
    detailedMetrics,
    extendModalActive,
    setExtendModalActive,
    handleOpenExtend,
  }
}
